import {
  Box,
  Container,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Text from "../../components/ui/Text";
import { DESKTOP_LAYOUTS, DESKTOP_SPACING } from "../../constants/spacing";
import SettingsDeleteAccount from "./DeleteAccount";
import SettingsEmail from "./Email";
import SettingsNotifications from "./Notifications";
import SettingsPassword from "./Password";

const SettingsTabContent = ({ activeTab }) => {
  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return <SettingsEmail isDesktop={true} />;
      case 1:
        return <SettingsPassword isDesktop={true} />;
      case 2:
        return <SettingsNotifications isDesktop={true} />;
      case 3:
        return <SettingsDeleteAccount isDesktop={true} />;
      default:
        return <SettingsEmail isDesktop={true} />;
    }
  };

  return <Box>{renderTabContent()}</Box>;
};

const Settings = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [activeTab, setActiveTab] = useState(0);

  const settingsItems = [
    { text: "Email", path: "/settings/email", component: "Email" },
    { text: "Password", path: "/settings/password", component: "Password" },
    {
      text: "Email notifications",
      path: "/settings/notifications",
      component: "Notifications",
    },
    {
      text: "Delete my account",
      path: "/settings/delete-account",
      component: "DeleteAccount",
    },
  ];

  const handleNavigation = (path) => {
    if (isDesktop) {
      // On desktop, update the URL without navigation
      window.history.pushState({}, "", path);
      const tabIndex = settingsItems.findIndex((item) => item.path === path);
      setActiveTab(tabIndex);
    } else {
      // On mobile, navigate to the page
      navigate(path);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const selectedItem = settingsItems[newValue];
    window.history.pushState({}, "", selectedItem.path);
  };

  // Desktop layout with tabs
  if (isDesktop) {
    return (
      <Container
        sx={DESKTOP_LAYOUTS.pageContainer(DESKTOP_SPACING.MAX_WIDTH.LG)}
      >
        {/* Page Header */}
        <Text variant="pageHeader" sx={DESKTOP_LAYOUTS.pageHeader}>
          Settings
        </Text>

        {/* Tabs */}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            mb: DESKTOP_SPACING.ELEMENT_GAP,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 400,
                color: "#666",
                minHeight: "48px",
                "&.Mui-selected": {
                  color: "#1890FF",
                  fontWeight: 500,
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#1890FF",
                height: "2px",
              },
            }}
          >
            {settingsItems.map((item) => (
              <Tab key={item.text} label={item.text} />
            ))}
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={DESKTOP_LAYOUTS.contentCard}>
          <SettingsTabContent
            activeTab={activeTab}
            settingsItems={settingsItems}
          />
        </Box>
      </Container>
    );
  }

  // Mobile layout (original)
  return (
    <Container maxWidth="sm" sx={{ mt: 2 }}>
      {/* Page Header */}
      <Text variant="pageHeader" sx={{ mb: "24px" }}>
        Settings
      </Text>

      {/* Settings List */}
      <List
        sx={{ p: 0, display: "flex", flexDirection: "column", gap: "16px" }}
      >
        {settingsItems.map((item) => (
          <ListItem
            key={item.text}
            onClick={() => handleNavigation(item.path)}
            sx={{
              p: "4px 16px",
              border: "1px solid #D4D4D4",
              borderRadius: "8px",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "#F6F6F6",
              },
            }}
          >
            <ListItemText
              primary={item.text}
              sx={{
                "& .MuiTypography-root": {
                  fontSize: "16px",
                  fontWeight: 400,
                  color: "#000",
                },
              }}
            />
          </ListItem>
        ))}
      </List>
    </Container>
  );
};

export default Settings;
